import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Button,
  Box,
  Stack,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import PolicyIcon from "@mui/icons-material/Policy";
import RouteIcon from "@mui/icons-material/Route";
import InsightsIcon from "@mui/icons-material/Insights";

// Same dark-card chrome as WarningDialog (accent bar + icon badge + header/
// footer), but themed for a commercial upsell instead of a destructive
// warning: orange/gold accent on enable, WarningDialog's red only borrowed
// for the disable confirmation (that one really is a "you're turning
// something off" moment).
const OrionModuleDialog = ({ open, mode = "enable", onConfirm, onCancel }) => {
  const isUnavailable = mode === "unavailable";
  const isEnable = mode === "enable" || isUnavailable;
  const accent = isEnable ? "#f26433" : "#ce361e";
  const accentGradient = isEnable
    ? "linear-gradient(90deg, #f26433 0%, #4cceac 100%)"
    : "linear-gradient(90deg, #ce361e 0%, #e05a3a 100%)";

  return (
    <Dialog
      open={open}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: "#1e1a1c",
          border: "1px solid rgba(255,255,255,0.07)",
          borderRadius: "12px",
          boxShadow: "0 24px 64px rgba(0,0,0,0.6)",
          minWidth: 380,
          overflow: "hidden",
        },
      }}
      slotProps={{
        backdrop: {
          sx: { backdropFilter: "blur(4px)", backgroundColor: "rgba(0,0,0,0.55)" },
        },
      }}
    >
      {/* Top accent bar */}
      <Box sx={{ height: 3, background: accentGradient }} />

      <DialogTitle
        sx={{
          backgroundColor: "transparent",
          px: 3,
          pt: 2.5,
          pb: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "8px",
            backgroundColor: isEnable ? "rgba(242,100,51,0.14)" : "rgba(206,54,30,0.12)",
            border: `1px solid ${isEnable ? "rgba(242,100,51,0.3)" : "rgba(206,54,30,0.25)"}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: 18, color: accent }} />
        </Box>
        <Typography
          sx={{
            fontSize: 18,
            fontWeight: 500,
            color: "#f0ece8",
            letterSpacing: "0.02em",
          }}
        >
          {isEnable ? "ORION MODULE" : "DISABLE ORION"}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ backgroundColor: "transparent", px: 3, py: 1.5, mt: 1 }}>
        {isEnable ? (
          <>
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 400,
                color: "rgba(240,236,232,0.75)",
                lineHeight: 1.6,
                mb: 2,
              }}
            >
              Orion gives you full visibility into your network: every asset is automatically
              enriched with OS, CVE and RISK data, and you unlock Network Zones &amp; Conduits —
              model your segmentation, assign risk levels, and monitor how OT and IT zones
              communicate.
            </Typography>

            <Stack spacing={1} sx={{ mb: 2.5 }}>
              <Stack direction="row" spacing={1.2} alignItems="flex-start">
                <InsightsIcon sx={{ fontSize: 17, color: accent, mt: "1px" }} />
                <Typography sx={{ fontSize: 13, color: "rgba(240,236,232,0.7)", lineHeight: 1.5 }}>
                  OS, CVE, RISK and TYPE data for every asset in Inventory
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1.2} alignItems="flex-start">
                <PolicyIcon sx={{ fontSize: 17, color: accent, mt: "1px" }} />
                <Typography sx={{ fontSize: 13, color: "rgba(240,236,232,0.7)", lineHeight: 1.5 }}>
                  Network Zones management — risk level, Purdue level, target SL
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1.2} alignItems="flex-start">
                <RouteIcon sx={{ fontSize: 17, color: accent, mt: "1px" }} />
                <Typography sx={{ fontSize: 13, color: "rgba(240,236,232,0.7)", lineHeight: 1.5 }}>
                  Conduits — track and govern how zones communicate with each other
                </Typography>
              </Stack>
            </Stack>

            {isUnavailable ? (
              <Box
                sx={{
                  borderRadius: "8px",
                  border: "1px solid rgba(206,54,30,0.4)",
                  backgroundColor: "rgba(206,54,30,0.1)",
                  px: 2,
                  py: 1.4,
                }}
              >
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#ff9a8a", mb: 0.5 }}>
                  Module not available
                </Typography>
                <Typography sx={{ fontSize: 12, color: "rgba(240,236,232,0.7)", lineHeight: 1.5 }}>
                  The module cannot be installed on the selected LECS. Please contact your
                  reference partner or write to us at{" "}
                  <a href="mailto:commerciale@cyberevolution.it" style={{ color: "#ffb38f" }}>
                    commerciale@cyberevolution.it
                  </a>{" "}
                  for more information about this feature.
                </Typography>
              </Box>
            ) : (
            <Box
              sx={{
                borderRadius: "8px",
                border: "1px solid rgba(242,100,51,0.25)",
                backgroundColor: "rgba(242,100,51,0.08)",
                px: 2,
                py: 1.4,
              }}
            >
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#ffb38f", mb: 0.5 }}>
                Free of charge until December 31, 2026
              </Typography>
              <Typography sx={{ fontSize: 12, color: "rgba(240,236,232,0.6)", lineHeight: 1.5 }}>
                Trial access is available to selected accounts with LECS PLATFORM MEDIUM, LARGE,
                and CORPORATE plans, subject to compatibility verification. Demo activation
                applies only to this LECS.
              </Typography>
            </Box>
            )}
          </>
        ) : (
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 400,
              color: "rgba(240,236,232,0.6)",
              lineHeight: 1.6,
            }}
          >
            Disabling ORION will hide OS, CVE, RISK and TYPE data from Inventory, and Network
            Zones &amp; Conduits management will no longer be accessible from this LECS. You can
            re-enable it at any time.
          </Typography>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          backgroundColor: "transparent",
          px: 3,
          py: 1.5,
          gap: 1,
          justifyContent: "flex-end",
          borderTop: "1px solid rgba(255,255,255,0.06)",
        }}
      >
        <Button
          variant="outlined"
          size="small"
          onClick={onCancel}
          sx={{
            color: "rgba(240,236,232,0.55)",
            borderColor: "rgba(255,255,255,0.1)",
            borderRadius: "7px",
            textTransform: "none",
            fontWeight: 500,
            fontSize: 12,
            px: 2,
            py: 0.5,
            minWidth: "unset",
            "&:hover": {
              borderColor: "rgba(255,255,255,0.2)",
              backgroundColor: "rgba(255,255,255,0.04)",
              color: "#f0ece8",
            },
          }}
        >
          {isUnavailable ? "Close" : "Cancel"}
        </Button>
        {!isUnavailable && (
        <Button
          variant="contained"
          size="small"
          onClick={onConfirm}
          sx={{
            backgroundColor: accent,
            borderRadius: "7px",
            textTransform: "none",
            fontWeight: 600,
            fontSize: 12,
            px: 2,
            py: 0.5,
            minWidth: "unset",
            boxShadow: `0 4px 14px ${isEnable ? "rgba(242,100,51,0.35)" : "rgba(206,54,30,0.35)"}`,
            "&:hover": {
              backgroundColor: isEnable ? "#d9531f" : "#b82f19",
              boxShadow: `0 4px 18px ${isEnable ? "rgba(242,100,51,0.5)" : "rgba(206,54,30,0.5)"}`,
            },
          }}
        >
          {isEnable ? "Enable ORION" : "Disable"}
        </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default OrionModuleDialog;
